import { useState, useEffect } from 'react';

export interface TaskFormData {
  title: string;
  description: string;
  type: 'feature' | 'bug';
  priority: string;
  requester_name: string;
  observations: string;
  estimate_hours: number;
  estimate_days: number;
  value: number;
  paid: boolean;
  sprint_desired: boolean;
  board_status: string;
  sprint_id: string | null;
}

interface Sprint {
  id: string;
  name: string;
  status: string;
}

interface TaskModalProps {
  task?: Partial<TaskFormData> & { id?: string };
  sprints: Sprint[];
  isAdmin: boolean;
  onClose: () => void;
  onSave: (data: TaskFormData) => void;
}

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Baixa', color: 'text-slate-500' },
  { value: 'medium', label: 'Média', color: 'text-orange-500' },
  { value: 'high', label: 'Alta', color: 'text-red-500' },
  { value: 'critical', label: 'Crítica', color: 'text-red-700' },
];

const BOARD_STATUS_OPTIONS = [
  { value: 'backlog', label: 'Backlog' },
  { value: 'todo', label: 'A Fazer' },
  { value: 'doing', label: 'Fazendo' },
  { value: 'validation', label: 'Em Validação' },
  { value: 'done', label: 'Concluído' },
];

export default function TaskModal({ task, sprints, isAdmin, onClose, onSave }: TaskModalProps) {
  const [form, setForm] = useState<TaskFormData>({
    title: task?.title || '',
    description: task?.description || '',
    type: task?.type || 'feature',
    priority: task?.priority || 'medium',
    requester_name: task?.requester_name || '',
    observations: task?.observations || '',
    estimate_hours: task?.estimate_hours || 0,
    estimate_days: task?.estimate_days || 0,
    value: task?.value || 0,
    paid: task?.paid || false,
    sprint_desired: task?.sprint_desired || false,
    board_status: task?.board_status || 'backlog',
    sprint_id: task?.sprint_id || null,
  });

  const set = (field: keyof TaskFormData, value: unknown) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <h2 className="text-slate-800 font-semibold text-base">
            {task?.id ? 'Editar Item' : 'Novo Item do Backlog'}
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Título */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Título *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="Descreva o item..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300"
              required
            />
          </div>

          {/* Tipo + Prioridade */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Tipo</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => set('type', 'feature')}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors cursor-pointer whitespace-nowrap ${form.type === 'feature' ? 'bg-violet-50 border-violet-300 text-violet-700' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                >
                  <i className="ri-add-circle-line"></i>
                  Nova Demanda
                </button>
                <button
                  type="button"
                  onClick={() => set('type', 'bug')}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors cursor-pointer whitespace-nowrap ${form.type === 'bug' ? 'bg-red-50 border-red-300 text-red-700' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                >
                  <i className="ri-bug-line"></i>
                  Bug/Correção
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Prioridade</label>
              <select
                value={form.priority}
                onChange={(e) => set('priority', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer"
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Descrição</label>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Detalhes do item..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300 resize-none"
            />
          </div>

          {/* Solicitante */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Solicitante</label>
            <input
              type="text"
              value={form.requester_name}
              onChange={(e) => set('requester_name', e.target.value)}
              placeholder="Nome do solicitante..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300"
            />
          </div>

          {/* Estimativa + Valor */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Horas estimadas</label>
              <input
                type="number"
                min={0}
                value={form.estimate_hours}
                onChange={(e) => set('estimate_hours', Number(e.target.value))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Dias estimados</label>
              <input
                type="number"
                min={0}
                step={0.5}
                value={form.estimate_days}
                onChange={(e) => set('estimate_days', Number(e.target.value))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Valor (R$)</label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={form.value}
                onChange={(e) => set('value', Number(e.target.value))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300"
              />
            </div>
          </div>

          {/* Pago + Sprint desejada */}
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.paid}
                onChange={(e) => set('paid', e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-cyan-500 focus:ring-cyan-300 cursor-pointer"
              />
              <span className="text-sm text-slate-600">Pago</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.sprint_desired}
                onChange={(e) => set('sprint_desired', e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-cyan-500 focus:ring-cyan-300 cursor-pointer"
              />
              <span className="text-sm text-slate-600">Desejo na próxima sprint</span>
            </label>
          </div>

          {/* Admin: status no board + sprint */}
          {isAdmin && (
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Status no Board</label>
                <select
                  value={form.board_status}
                  onChange={(e) => set('board_status', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer"
                >
                  {BOARD_STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Sprint</label>
                <select
                  value={form.sprint_id || ''}
                  onChange={(e) => set('sprint_id', e.target.value || null)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer"
                >
                  <option value="">Sem sprint</option>
                  {sprints.map((s) => (
                    <option key={s.id} value={s.id}>{s.name} ({s.status === 'active' ? 'Ativa' : s.status === 'future' ? 'Futura' : 'Encerrada'})</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Observações */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Observações</label>
            <textarea
              value={form.observations}
              onChange={(e) => set('observations', e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Observações adicionais..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300 resize-none"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form=""
            onClick={handleSubmit}
            className="px-5 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            {task?.id ? 'Salvar alterações' : 'Criar item'}
          </button>
        </div>
      </div>
    </div>
  );
}
